import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import IconCaretDown from '@persona-id/design-tokens/icons/ChevronDownIcon';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { isEqual } from 'lodash/fp';
import { Dispatch, Fragment, SetStateAction, useEffect, useState, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { samplePrimitiveResolver } from 'dashboard/components/WorkflowControlPanel/TriggerLiveTest/TriggerLiveTest';
import TriggerLiveTestPayloadField from 'dashboard/components/WorkflowControlPanel/TriggerLiveTest/TriggerLiveTestPayloadField';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { JsonSchemaTypes } from 'lib/json-schema';

const NameCard = styled.div`
  display: flex;
  height: 32px;
  width: 35%;
  border: 1px solid ${Color.CoreSlate800};
  padding: 1px;
  background-color: ${Color.CoreSlate50};
  align-self: baseline;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const TypeCard = styled.div`
  display: flex;
  height: 32px;
  width: 10%;
  border: 1px solid ${Color.CoreSlate800};
  padding: 1px;
  background-color: ${Color.CoreSlate50};
  align-self: baseline;
  border-radius: ${BORDER_RADIUSES.rounded};
  margin-left: ${Spacing[2]};
`;

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-basis: auto;
  padding: 1px;
`;

const StyledButton = styled(Button)`
  margin-top: ${Spacing[4]};
`;

const Nest = styled.div<{ depth: number }>`
  margin: ${Spacing[2]} 0;
  padding: ${Spacing[2]} ${Spacing[5]};
  background-color: ${({ depth }) => (depth % 2 === 0 ? Color.CoreSlate100 : Color.CoreSlate50)};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const WithLeftSpace = styled.div`
  margin-left: ${Spacing[5]};
`;

const TextFieldContainer = styled.div`
  height: ${({ theme }) => theme.element.height.xs};
  ${paragraphMixins.md};
  margin-left: ${Spacing[2]};
  align-self: baseline;
  line-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeFieldContainer = styled.div`
  height: ${({ theme }) => theme.element.height.xs};
  ${paragraphMixins.md};
  margin-left: ${Spacing[2]};
  align-self: baseline;
  line-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrayRowContaier = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: ${Spacing[2]};
`;

const ExpandButton = styled.div`
  cursor: pointer;
  &:focus {
    & rec[fill],
    & path[fill] {
      fill: ${Color.CorePurple800};
    }
  }
`;

const ExpandButtonContainer = styled.div`
  width: ${Spacing[5]};
  align-self: baseline;
`;

const StyledIconCaret = styled(({ expanded, menuItem, ...props }) => <IconCaretDown {...props} />)<{
  expanded: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0deg' : '-90deg')});
  justify-self: center;

  rect[fill],
  path[fill] {
    fill: ${({ theme }) => theme.color.text.primary};
  }
`;

const DeleteButton = styled.div`
  margin-left: ${Spacing[2]};
  cursor: pointer;
  // For general browser, show DeleteButton per hovering Row but keep existence for a11y
  opacity: 0;
  &:hover,
  &:focus {
    opacity: 1;
  }
  &:focus {
    color: ${Color.CorePurple800};
  }
`;

export const REACT_SELECT_PORTAL_ID_PREFIX = 'trigger-config-api-code-live-test-payload-editor';

interface TriggerLiveTestPayloadEditorProps {
  payloadSample: Record<string, unknown>;
  updatePayloadSample: Dispatch<SetStateAction<Record<string, unknown>>>;
  currentDepth?: number;
}

const TriggerLiveTestPayloadEditor: VFC<TriggerLiveTestPayloadEditorProps> = (props) => (
  <Well>
    <InnerTriggerLiveTestPayloadEditor {...props} />
  </Well>
);

const InnerTriggerLiveTestPayloadEditor: VFC<TriggerLiveTestPayloadEditorProps> = ({
  payloadSample,
  updatePayloadSample,
  currentDepth = 0,
}) => {
  const [internalEntries, setInternalEntries] = useState<[string, unknown][]>(
    Object.entries(payloadSample),
  );

  const [expandedIndexes, updateExpandedIndexes] = useState<number[]>([]);

  const toggleExpansion = (index: number) => {
    updateExpandedIndexes((currentExpandedIndexes) =>
      currentExpandedIndexes.includes(index)
        ? currentExpandedIndexes.filter((i) => i !== index)
        : [...currentExpandedIndexes, index],
    );
  };

  useEffect(() => {
    if (!isEqual(Object.entries(payloadSample), internalEntries)) {
      updatePayloadSample(Object.fromEntries(internalEntries));
    }
  }, [internalEntries, payloadSample, updatePayloadSample]);

  return (
    <>
      {internalEntries.map(([fieldKey, fieldValue], index) => {
        return (
          <Fragment key={index}>
            <Spacer height={index !== 0 ? 1 : 0} />
            <Row>
              <ExpandButtonContainer>
                {typeof fieldValue === JsonSchemaTypes.Object && (
                  <ExpandButton
                    id={`${REACT_SELECT_PORTAL_ID_PREFIX}__expand-object-${currentDepth}-${index}`}
                    role='button'
                    aria-label={`${
                      expandedIndexes.includes(index) ? 'Hide' : 'Show'
                    } nested properties`}
                    tabIndex={0}
                    onClick={() => {
                      toggleExpansion(index);
                    }}
                    onKeyPress={(e) => {
                      if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                        toggleExpansion(index);
                        e.preventDefault();
                      }
                    }}
                  >
                    <StyledIconCaret expanded={expandedIndexes.includes(index)} />
                  </ExpandButton>
                )}
              </ExpandButtonContainer>
              <NameCard>
                <TextFieldContainer aria-label='Card field name'>{fieldKey}</TextFieldContainer>
              </NameCard>
              <TypeCard>
                <TypeFieldContainer aria-label='Card field type'>
                  {Array.isArray(fieldValue) ? 'array' : typeof fieldValue}
                </TypeFieldContainer>
              </TypeCard>
              <TriggerLiveTestPayloadField
                value={fieldValue}
                updateValue={(value) => {
                  setInternalEntries((currentEntries: [string, unknown][]) =>
                    Object.assign([], currentEntries, {
                      [index]: [currentEntries[index][0], value],
                    }),
                  );
                }}
                widthPercentage={45}
              />
            </Row>
            {typeof fieldValue === JsonSchemaTypes.Object &&
              !Array.isArray(fieldValue) &&
              expandedIndexes.includes(index) && (
                <Nest depth={currentDepth}>
                  <InnerTriggerLiveTestPayloadEditor
                    payloadSample={fieldValue as Record<string, unknown>}
                    updatePayloadSample={(newSchema) => {
                      setInternalEntries((currentEntries: [string, unknown][]) =>
                        Object.assign([], currentEntries, {
                          [index]: [currentEntries[index][0], newSchema],
                        }),
                      );
                    }}
                    currentDepth={currentDepth + 1}
                  />
                </Nest>
              )}
            {typeof fieldValue === JsonSchemaTypes.Object &&
              Array.isArray(fieldValue) &&
              expandedIndexes.includes(index) && (
                <Nest depth={currentDepth}>
                  <ArrayRowContaier
                    key={`${REACT_SELECT_PORTAL_ID_PREFIX}-array-container-${currentDepth}-${index}`}
                  >
                    {fieldValue.map((value, idx) => {
                      return (
                        <ArrayRowContaier
                          id={`${REACT_SELECT_PORTAL_ID_PREFIX}-array-entry-${currentDepth}-${idx}`}
                          key={`${REACT_SELECT_PORTAL_ID_PREFIX}-array-entry-${currentDepth}-${idx}`}
                        >
                          <TriggerLiveTestPayloadField
                            value={value}
                            updateValue={(value) => {
                              const innerArray = Object.assign([], fieldValue, { [idx]: value });
                              setInternalEntries((currentEntries: [string, unknown][]) =>
                                Object.assign([], currentEntries, {
                                  [index]: [currentEntries[index][0], innerArray],
                                }),
                              );
                            }}
                            widthPercentage={null}
                          />
                          {fieldValue.length > 1 && (
                            <DeleteButton
                              id={`${REACT_SELECT_PORTAL_ID_PREFIX}-delete-array-${currentDepth}-${idx}`}
                              role='button'
                              aria-label='Delete this property'
                              tabIndex={0}
                              onClick={() => {
                                const innerArray = [
                                  ...fieldValue.slice(0, idx),
                                  ...fieldValue.slice(idx + 1),
                                ];
                                setInternalEntries((currentEntries: [string, unknown][]) =>
                                  Object.assign([], currentEntries, {
                                    [index]: [currentEntries[index][0], innerArray],
                                  }),
                                );
                              }}
                            >
                              <XIcon />
                            </DeleteButton>
                          )}
                        </ArrayRowContaier>
                      );
                    })}
                  </ArrayRowContaier>
                  <WithLeftSpace>
                    <StyledButton
                      onClick={() => {
                        const innerArray = Object.assign(
                          [],
                          fieldValue,
                          fieldValue.push(
                            samplePrimitiveResolver(typeof fieldValue[0] as JsonSchemaTypes),
                          ),
                        );
                        setInternalEntries((currentEntries: [string, unknown][]) =>
                          Object.assign([], currentEntries, {
                            [index]: [currentEntries[index][0], innerArray],
                          }),
                        );
                      }}
                      id={`${REACT_SELECT_PORTAL_ID_PREFIX}-add-array-property-${currentDepth}`}
                      type='button'
                      variant='inverse'
                      width='auto'
                      prefixIcon={<IconPlus />}
                    >
                      Add property
                    </StyledButton>
                  </WithLeftSpace>
                </Nest>
              )}
          </Fragment>
        );
      })}
      {internalEntries.length === 0 && <WithLeftSpace>No Properties</WithLeftSpace>}
    </>
  );
};

export default TriggerLiveTestPayloadEditor;
